import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Image, Prohibit, ShoppingCartSimple, TrendUp } from "@phosphor-icons/react";
import { containerProxyUrl, livePhotoUrl, formatCellValue, isContainerField } from "@/lib/fmUtils";
import { ProductHoverPreview } from "@/components/ProductHoverPreview";
import { ChinaQuoteLink } from "@/components/ChinaQuoteLink";

export default function ProductTable({
  layout,
  columns,
  records,
  fieldTypeMap,
  loading,
  onRowClick,
  onAddToCart,
  photoVersion,
  selectable = false,
  selectedIds,
  onToggleSelect,
  showHoverPreview = true,
  showBestBadge = false,
  showVariantSummary = false,
}) {
  if (loading) {
    return (
      <div className="flex flex-col gap-1 p-4" data-testid="product-table-skeleton">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full rounded-sm" />
        ))}
      </div>
    );
  }

  if (!records || records.length === 0) {
    return (
      <div
        className="flex h-64 items-center justify-center text-sm text-zinc-500"
        data-testid="product-table-empty-state"
      >
        No records found.
      </div>
    );
  }

  return (
    <div className="overflow-auto">
      <Table data-testid="product-table">
        <TableHeader className="sticky top-0 z-10 bg-zinc-50">
          <TableRow className="border-b border-zinc-300">
            {columns.map((col) => {
              const meta = fieldTypeMap[col.key];
              const isNumeric = meta?.result === "number";
              return (
                <TableHead
                  key={col.key}
                  className={`whitespace-nowrap py-2 text-xs uppercase tracking-[0.05em] text-zinc-500 ${
                    isNumeric ? "text-right" : "text-left"
                  }`}
                >
                  {col.label}
                </TableHead>
              );
            })}
            {showVariantSummary && (
              <TableHead className="whitespace-nowrap py-2 text-xs uppercase tracking-[0.05em] text-zinc-500">
                Price List
              </TableHead>
            )}
            <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((rec, idx) => (
            <TableRow
              key={rec.recordId}
              onClick={() => onRowClick(rec, idx)}
              className="cursor-pointer border-b border-zinc-200 transition-colors duration-150 hover:bg-zinc-100"
              data-testid={`product-row-${rec.recordId}`}
            >
              {columns.map((col) => {
                const meta = fieldTypeMap[col.key];
                const value = rec.fieldData?.[col.key];
                if (isContainerField(meta, col.key)) {
                  const src = value ? (layout ? livePhotoUrl(layout, rec.recordId, col.key, photoVersion) : containerProxyUrl(value)) : null;
                  const bestBadge =
                    showBestBadge && rec._best_selling ? (
                      <span
                        className="mb-0.5 flex w-fit items-center gap-0.5 rounded-sm bg-amber-500 px-1 py-px text-[8px] font-bold uppercase tracking-wide text-white"
                        data-testid={`best-selling-badge-${rec.recordId}`}
                      >
                        <TrendUp size={9} weight="bold" /> Best
                      </span>
                    ) : null;
                  const blockedBadge = rec.block ? (
                    <span
                      className="mb-0.5 flex w-fit items-center gap-0.5 rounded-sm bg-red-600 px-1 py-px text-[8px] font-bold uppercase tracking-wide text-white"
                      data-testid={`blocked-badge-${rec.recordId}`}
                      title={`Blocked by ${rec.block.blocked_by || "-"}${
                        rec.block.blocked_at ? ` on ${new Date(rec.block.blocked_at).toLocaleDateString()}` : ""
                      }`}
                    >
                      <Prohibit size={9} weight="bold" /> Blocked
                      <span className="font-medium normal-case tracking-normal">
                        · {rec.block.blocked_by || "-"}
                        {rec.block.blocked_at ? ` · ${new Date(rec.block.blocked_at).toLocaleDateString()}` : ""}
                      </span>
                    </span>
                  ) : null;
                  return (
                    <TableCell key={col.key} className="py-1.5" onClick={(e) => e.stopPropagation()}>
                      {bestBadge}
                      {blockedBadge}
                      {showHoverPreview ? (
                        <ProductHoverPreview
                          imgSrc={src}
                          description={rec.fieldData?.description}
                          itemNo={rec.fieldData?.["item no"]}
                          variants={rec.portalData?.product}
                        >
                          <div className="inline-block cursor-pointer" onClick={() => onRowClick(rec, idx)}>
                            {src ? (
                              <img
                                src={src}
                                alt="thumbnail"
                                loading="lazy"
                                decoding="async"
                                className="h-24 w-24 rounded-sm border border-zinc-300 bg-zinc-50 object-contain"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="flex h-24 w-24 items-center justify-center rounded-sm border border-zinc-200 bg-zinc-50">
                                <Image size={16} className="text-zinc-400" />
                              </div>
                            )}
                          </div>
                        </ProductHoverPreview>
                      ) : (
                        <div className="inline-block cursor-pointer" onClick={() => onRowClick(rec, idx)}>
                          {src ? (
                            <img
                              src={src}
                              alt="thumbnail"
                              loading="lazy"
                              decoding="async"
                              className="h-24 w-24 rounded-sm border border-zinc-300 bg-zinc-50 object-contain"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="flex h-24 w-24 items-center justify-center rounded-sm border border-zinc-200 bg-zinc-50">
                              <Image size={16} className="text-zinc-400" />
                            </div>
                          )}
                        </div>
                      )}
                    </TableCell>
                  );
                }
                const isNumeric = meta?.result === "number";
                return (
                  <TableCell
                    key={col.key}
                    className={`py-1.5 text-sm text-zinc-800 ${
                      isNumeric ? "text-right font-mono-data" : "text-left"
                    }`}
                  >
                    {col.key === "item no" ? (
                      <ChinaQuoteLink itemNo={value} />
                    ) : (
                      formatCellValue(meta, value)
                    )}
                  </TableCell>
                );
              })}
              {showVariantSummary && (
                <TableCell className="py-1.5 align-top" data-testid={`variant-summary-cell-${rec.recordId}`}>
                  {(rec.portalData?.product || []).length > 0 ? (
                    <table className="w-44 text-[11px]">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-[0.03em] text-zinc-400">
                          <th className="text-left font-normal">Size</th>
                          <th className="text-right font-normal">QTY</th>
                          <th className="text-right font-normal">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rec.portalData.product.map((v) => (
                          <tr key={v.recordId} className="border-t border-zinc-100 text-zinc-700">
                            <td className="py-0.5">{(v["product::Size"] || "").trim() || "-"}</td>
                            <td className="py-0.5 text-right font-mono-data">
                              {v["product::inventory qty"] != null ? Math.round(v["product::inventory qty"]) : "-"}
                            </td>
                            <td className="py-0.5 text-right font-mono-data">
                              ${(Number(v["product::Price"]) || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <span className="text-xs text-zinc-400">-</span>
                  )}
                </TableCell>
              )}
              <TableCell className="py-1.5 text-right" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-end gap-1.5">
                  {selectable && (
                    <Checkbox
                      checked={!!selectedIds?.has(rec.recordId)}
                      onCheckedChange={() => onToggleSelect(rec)}
                      className="h-5 w-5 rounded-none border-zinc-400"
                      data-testid={`select-product-checkbox-${rec.recordId}`}
                    />
                  )}
                  {onAddToCart && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-auto rounded-sm border-zinc-300 p-1.5 hover:bg-blue-600 hover:text-white"
                      onClick={() => onAddToCart(rec)}
                      title="Add to Quote"
                      data-testid={`add-to-cart-list-button-${rec.recordId}`}
                    >
                      <ShoppingCartSimple size={14} />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
